import { useCallback, useEffect, useRef, useState } from "react";
import { API_BASE } from "../core/config/api-url";

// Primitive values accepted for query params.
type Primitive = string | number | boolean | Date | null | undefined;
type QueryValue = Primitive;
// type RequestBody = Record<string, unknown> | FormData | undefined;
type RequestBody = object | FormData | undefined;
type FileLike = File | Blob;

// Supported HTTP methods for this hook.
export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE" | "HEAD" | "OPTIONS";

// Files grouped by field name, supporting single or multiple files per field.
export type FileFields = Record<string, FileLike | FileLike[] | null | undefined>;

// Error shape aligned to backend ApiErrorDto.
export type ApiErrorDto = {
	code?: string;
	message?: string;
	details?: Array<{ field?: string; message?: string; code?: string }>;
	timestamp?: string;
	path?: string;
	requestId?: string;
};

type ApiEnvelope<TData> = {
	success: boolean;
	data: TData | null;
	error: ApiErrorDto | null;
	meta?: unknown;
};

type UseFetchConfig<TData, TBody extends RequestBody = RequestBody> = {
	endpoint: string;
	method?: HttpMethod;
	body?: TBody;
	files?: FileFields;
	query?: Record<string, QueryValue>;
	headers?: HeadersInit;
	credentials?: RequestCredentials;
	auto?: boolean;
	initialData?: TData | null;
};

type ExecuteOptions<TBody extends RequestBody = RequestBody> = {
	body?: TBody;
	files?: FileFields;
	query?: Record<string, QueryValue>;
	headers?: HeadersInit;
	endpoint?: string;
	method?: HttpMethod;
	credentials?: RequestCredentials;
};

// Narrow unknown to object for safe property checks.
function isRecord(value: unknown): value is Record<string, unknown> {
	return value !== null && typeof value === "object";
}

// Detect backend standardized envelope: { success, data, error, meta }.
function isApiEnvelope<TData>(value: unknown): value is ApiEnvelope<TData> {
	return (
		isRecord(value) &&
		typeof value.success === "boolean" &&
		"data" in value &&
		"error" in value
	);
}

// Quickly determines if we should send multipart/form-data.
function hasFiles(files?: FileFields): boolean {
	if (!files) return false;

	return Object.values(files).some((entry) => {
		if (!entry) return false;
		if (Array.isArray(entry)) return entry.length > 0;
		return true;
	});
}

// Converts body values into FormData-compatible entries.
// - Dates are sent as ISO string
// - Arrays append multiple values with same key
// - Objects are stringified
function appendFormValue(formData: FormData, key: string, value: unknown): void {
	if (value === null || value === undefined) return;

	if (value instanceof Date) {
		formData.append(key, value.toISOString());
		return;
	}

	if (value instanceof File || value instanceof Blob) {
		formData.append(key, value);
		return;
	}

	if (Array.isArray(value)) {
		value.forEach((item) => appendFormValue(formData, key, item));
		return;
	}

	if (typeof value === "object") {
		formData.append(key, JSON.stringify(value));
		return;
	}

	formData.append(key, String(value));
}

// Appends one or many files for each field expected by endpoint.
function appendFiles(formData: FormData, files?: FileFields): void {
	if (!files) return;

	Object.entries(files).forEach(([field, value]) => {
		if (!value) return;

		if (Array.isArray(value)) {
			value.forEach((file) => {
				if (file) formData.append(field, file);
			});
			return;
		}

		formData.append(field, value);
	});
}

// Builds full request URL from API base + endpoint tail + optional query params.
function buildUrl(endpoint: string, query?: Record<string, QueryValue>): string {
	const normalizedEndpoint = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
	const url = new URL(`${API_BASE}${normalizedEndpoint}`);

	if (query) {
		Object.entries(query).forEach(([key, value]) => {
			if (value === null || value === undefined) return;
			if (value instanceof Date) {
				url.searchParams.append(key, value.toISOString());
				return;
			}
			url.searchParams.append(key, String(value));
		});
	}

	return url.toString();
}

// Resolves best error message from backend envelope or generic payload.
function getErrorMessage(payload: unknown, status: number): string {
	if (isApiEnvelope(payload)) {
		const message = payload.error?.message;
		if (typeof message === "string" && message.trim()) return message;
	}

	if (isRecord(payload)) {
		const message = payload.message;
		if (typeof message === "string" && message.trim()) return message;
		if (Array.isArray(message) && message.length > 0) {
			return message
				.filter(
					(item): item is string =>
						typeof item === "string" && item.trim().length > 0,
				)
				.join("\n");
		}
	}

	return `HTTP ${status}`;
}

// Chooses JSON vs multipart body based on body/files combination.
function buildRequestBody(body: RequestBody, files?: FileFields): BodyInit | undefined {
	if (body instanceof FormData) {
		// Clone incoming FormData to avoid mutating caller data between executions.
		const formData = new FormData();
		body.forEach((value, key) => {
			formData.append(key, value);
		});
		appendFiles(formData, files);
		return formData;
	}

	if (hasFiles(files)) {
		// If there are files, force multipart body and include plain fields.
		const formData = new FormData();

		if (isRecord(body)) {
			Object.entries(body).forEach(([key, value]) =>
				appendFormValue(formData, key, value),
			);
		}

		appendFiles(formData, files);
		return formData;
	}

	if (body === undefined) return undefined;
	return JSON.stringify(body);
}

export function useFetch<TData = unknown, TBody extends RequestBody = RequestBody>(
	config: UseFetchConfig<TData, TBody>,
) {
	const {
		endpoint,
		method = "GET",
		body,
		files,
		query,
		headers,
		credentials,
		auto = method === "GET",
		initialData = null,
	} = config;

	const isMountedRef = useRef(true);
	const abortControllerRef = useRef<AbortController | null>(null);

	const [data, setData] = useState<TData | null>(initialData);
	const [meta, setMeta] = useState<unknown>(null);
	const [error, setError] = useState<ApiErrorDto | null>(null);
	const [loading, setLoading] = useState(false);
	const [status, setStatus] = useState<number | null>(null);

	// Manually abort current request.
	const abort = useCallback(() => {
		abortControllerRef.current?.abort();
		abortControllerRef.current = null;
	}, []);

	const reset = useCallback(() => {
		if (!isMountedRef.current) return;
		// Reset hook state to initial values.
		setData(initialData);
		setMeta(null);
		setError(null);
		setLoading(false);
		setStatus(null);
	}, [initialData]);

	const execute = useCallback(
		async (overrides?: ExecuteOptions<TBody>): Promise<TData | null> => {
			// Merge base config with per-call overrides.
			const nextEndpoint = overrides?.endpoint ?? endpoint;
			const nextMethod = (overrides?.method ?? method).toUpperCase() as HttpMethod;
			const nextBody = overrides?.body ?? body;
			const nextFiles = overrides?.files ?? files;
			const nextQuery = overrides?.query ?? query;
			const nextHeaders = new Headers(headers);
			const extraHeaders = new Headers(overrides?.headers);
			const nextCredentials = overrides?.credentials ?? credentials ?? "include";

			extraHeaders.forEach((value, key) => nextHeaders.set(key, value));

			// GET/HEAD should not carry a body.
			const requestBody = ["GET", "HEAD"].includes(nextMethod)
				? undefined
				: buildRequestBody(nextBody, nextFiles);

			// Let browser set multipart boundary automatically when body is FormData.
			if (
				!(requestBody instanceof FormData) &&
				requestBody !== undefined &&
				!nextHeaders.has("Content-Type")
			) {
				nextHeaders.set("Content-Type", "application/json");
			}
			// Cancel previous in-flight request before starting a new one.

			abortControllerRef.current?.abort();
			const controller = new AbortController();
			abortControllerRef.current = controller;

			if (isMountedRef.current) {
				setLoading(true);
				setError(null);
			}

			try {
				const response = await fetch(buildUrl(nextEndpoint, nextQuery), {
					method: nextMethod,
					headers: nextHeaders,
					body: requestBody,
					cache: "no-store",
					signal: controller.signal,
					credentials: nextCredentials,
				});

				let payload: unknown = null;
				const contentType =
					response.headers.get("content-type")?.toLowerCase() ?? "";

				if (contentType.includes("application/json")) {
					try {
						payload = await response.json();
					} catch {
						payload = null;
					}
				} else {
					const textPayload = await response.text();
					payload = textPayload || null;
				}

				if (!response.ok) {
					const message = getErrorMessage(payload, response.status);
					const backendError = isApiEnvelope(payload) ? payload.error : null;
					const normalizedError: ApiErrorDto = {
						code: backendError?.code,
						message,
						details: backendError?.details,
						timestamp: backendError?.timestamp,
						path: backendError?.path,
						requestId: backendError?.requestId,
					};
					// Safely parse JSON (if malformed, fallback to null).

					if (isMountedRef.current) {
						setStatus(response.status);
						setError(normalizedError);
					}

					throw new Error(message);
				}
				// HTTP-level errors: normalize backend error payload.

				if (isApiEnvelope<TData>(payload)) {
					if (!payload.success) {
						const message = payload.error?.message || "Request failed";
						if (isMountedRef.current) {
							// Application-level envelope errors, even if HTTP status is 2xx.
							setStatus(response.status);
							setError(payload.error ?? { message });
						}
						throw new Error(message);
					}

					if (isMountedRef.current) {
						setStatus(response.status);
						setData(payload.data);
						// Success case aligned with backend standardization.
						setMeta(payload.meta ?? null);
					}

					return payload.data;
				}

				if (isMountedRef.current) {
					setStatus(response.status);
					setData((payload as TData) ?? null);
					setMeta(null);
				}

				return (payload as TData) ?? null;
			} catch (caughtError) {
				if ((caughtError as Error).name === "AbortError") {
					return null;
				}
				// Swallow abort as controlled flow.

				if (isMountedRef.current) {
					setError(
						(prevError) =>
							prevError ?? {
								message:
									caughtError instanceof Error
										? caughtError.message
										: "An unknown error occurred",
							},
					);
				}

				throw caughtError;
			} finally {
				if (isMountedRef.current && abortControllerRef.current === controller) {
					setLoading(false);
					abortControllerRef.current = null;
					// Ensure loading is cleared only for current active request.
				}
			}
		},
		[endpoint, method, body, files, query, headers, credentials],
	);

	useEffect(() => {
		isMountedRef.current = true;

		return () => {
			isMountedRef.current = false;
			abortControllerRef.current?.abort();
			abortControllerRef.current = null;
			// Cleanup on unmount to avoid setState on unmounted component.
		};
	}, []);

	useEffect(() => {
		if (!auto) return;
		void execute().catch(() => null);
	}, [auto, execute]);
	// Auto-run for default GET behavior unless auto=false.

	return {
		data,
		meta,
		error,
		loading,
		status,
		execute,
		abort,
		reset,
	};
}
